/**
 * Megamenu Alpine.js Component
 *
 * Handles megamenu functionality including:
 * - Injection into header navigation
 * - Show/hide based on menu item hover/click
 * - Target specific menu items by title or handle
 * - Mobile/desktop responsive behavior
 * - Auto-hide functionality
 * - Animation support
 * - Gap-aware mouse tracking
 */
export default {
  name: 'megamenu',
  component(settings) {
    return {
      // Core megamenu state
      isVisible: false,

      // Backdrop settings
      enableBackdrop: settings.enable_backdrop || false,

      // Menu targeting
      targetMenuTitle: settings.target_menu_title || '',
      targetMenuItems: settings.target_menu_handles
        ? settings.target_menu_handles.split(',').map((item) => item.trim())
        : [],

      // Auto-hide timer
      hideTimeout: null,
      hoverLeaveTimeout: null,
      isTransitioning: false,

      // Block builder state
      isBlockBuilderActive: false,

      // Event listeners storage for cleanup
      eventListeners: [],

      // Mouse tracking for gap handling
      isMouseInSafeZone: false,
      activeMenuElement: null,

      // Static registry for all megamenu instances
      static: {
        instances: new Set()
      },

      // Computed: Should megamenu be visible based on device settings and state
      get shouldBeVisible() {
        // Force reactivity on resize
        this.renderTypeKey;

        if (!this.isVisible) return false;
      },

      // Initialize megamenu
      init() {
        this.$nextTick(() => this.$el.classList.remove('hidden'));

        // Register this instance
        this.static.instances.add(this);

        // Inject megamenu into header if selector is provided
        this.injectIntoHeader();

        // Set up menu hover listeners
        this.setupMenuListeners();

        // Handle resize events for responsive behavior
        this.setupResizeListener();

        // Set up global mouse tracking for gap handling
        this.setupGlobalMouseTracking();

        // Set up click outside listener
        this.setupClickOutsideListener();

        // Set up block builder state listener
        this.setupBlockBuilderListener();

        // Cleanup on destroy
        this.$el.addEventListener('alpine:destroyed', () => {
          this.static.instances.delete(this);
        });

        this.setupReparentHeightCalculation();
      },

      // Inject megamenu into header navigation
      injectIntoHeader() {
        if (!settings.reparent_to_selector) return;

        // Wait for DOM to be ready
        setTimeout(() => {
          const headerContainer = document.querySelector(settings.reparent_to_selector);
          const megamenuElement = this.$el;

          if (headerContainer && megamenuElement) {
            // Remove from current position and inject into header
            headerContainer.appendChild(megamenuElement);

            // Ensure header container has relative positioning
            const computedStyle = window.getComputedStyle(headerContainer);
            if (computedStyle.position === 'static') {
              headerContainer.style.position = 'relative';
            }
          } else {
            console.warn('Could not inject megamenu - header container not found:', settings.reparent_to_selector);
          }
        }, 100);
      },

      // Set up global mouse tracking to handle gaps
      setupGlobalMouseTracking() {
        const mouseMoveHandler = (e) => {
          if (this.isVisible && this.activeMenuElement) {
            const wasInSafeZone = this.isMouseInSafeZone;
            this.isMouseInSafeZone = this.isMouseInSafeArea(e, this.activeMenuElement);
            
            // If mouse just left the safe zone, trigger hide
            if (wasInSafeZone && !this.isMouseInSafeZone) {
              this.handleIntelligentHide();
            }
          }
        };

        document.addEventListener('mousemove', mouseMoveHandler);

        this.eventListeners.push({
          element: document,
          events: [{ type: 'mousemove', handler: mouseMoveHandler }]
        });
      },

      // Set up click outside listener to close megamenu
      setupClickOutsideListener() {
        const clickHandler = (e) => {
          if (!this.isVisible) return;
          
          // Check if click is outside the megamenu and the menu item that triggers it
          const isClickInsideMegamenu = this.$el.contains(e.target);
          const isClickOnTriggerMenuItem = this.activeMenuElement && this.activeMenuElement.contains(e.target);
          
          // Also check if click is on any menu item that could trigger a megamenu
          const clickedMenuItem = e.target.closest('nav a, .menu a, [data-menu-item]');
          const isClickOnMenuItemInHeader = clickedMenuItem && clickedMenuItem.closest('[x-data*="header"]');
          
          if (!isClickInsideMegamenu && !isClickOnTriggerMenuItem && !isClickOnMenuItemInHeader) {
            this.hideMegamenu();
            this.activeMenuElement = null;
          }
        };

        document.addEventListener('click', clickHandler);

        this.eventListeners.push({
          element: document,
          events: [{ type: 'click', handler: clickHandler }]
        });
      },

      // Set up block builder state listener to disable megamenu when active
      setupBlockBuilderListener() {
        const blockBuilderStateHandler = (e) => {
          const { state } = e.detail;
          
          // Update block builder active state
          this.isBlockBuilderActive = state === 'visible';
          
          // Hide megamenu if block builder becomes active
          if (this.isBlockBuilderActive && this.isVisible) {
            this.hideMegamenu();
            this.activeMenuElement = null;
          }
        };

        window.addEventListener('block-builder-state-change', blockBuilderStateHandler);

        this.eventListeners.push({
          element: window,
          events: [{ type: 'block-builder-state-change', handler: blockBuilderStateHandler }]
        });
      },

      // Check if mouse is in the safe area (menu item + gap + megamenu content)
      isMouseInSafeArea(mouseEvent, menuElement) {
        const mouseX = mouseEvent.clientX;
        const mouseY = mouseEvent.clientY;

        // Get menu item bounds
        const menuRect = menuElement.getBoundingClientRect();

        // Get megamenu content bounds (safe zone, not the full-width container)
        const megamenuSafeZone = this.$el.querySelector('.megamenu-safe-zone');
        if (!megamenuSafeZone) {
          return false;
        }
        const megamenuRect = megamenuSafeZone.getBoundingClientRect();

        // Check if mouse is in menu item
        const inMenuItem = (
          mouseX >= menuRect.left && mouseX <= menuRect.right && 
          mouseY >= menuRect.top && mouseY <= menuRect.bottom
        );

        // Check if mouse is in megamenu content area
        const inMegamenuContent = (
          mouseX >= megamenuRect.left && mouseX <= megamenuRect.right && 
          mouseY >= megamenuRect.top && mouseY <= megamenuRect.bottom
        );

        // Define gap area between menu item and megamenu (if menu is above megamenu)
        const gapTolerance = 10; // pixels
        const inGap = (
          mouseX >= Math.min(menuRect.left, megamenuRect.left) && 
          mouseX <= Math.max(menuRect.right, megamenuRect.right) &&
          mouseY >= menuRect.bottom && 
          mouseY <= megamenuRect.top + gapTolerance
        );

        return inMenuItem || inMegamenuContent || inGap;
      },

      // Set up menu item hover/click listeners
      setupMenuListeners() {
        const attemptSetup = (attempt = 1, maxAttempts = 10) => {
          // Listen for menu item interactions
          // Prioritize menu items inside reparent_to_selector if available
          let menuItems;
          if (settings.reparent_to_selector) {
            const reparentContainer = document.querySelector(settings.reparent_to_selector);
            if (reparentContainer) {
              menuItems = reparentContainer.querySelectorAll('nav a, .menu a, [data-menu-item]');
            }
          }

          // Wait for React to render menu items
          if (menuItems.length === 0 && attempt < maxAttempts) {
            setTimeout(() => attemptSetup(attempt + 1, maxAttempts), 200);
            return;
          }

          if (menuItems.length === 0) {
            console.warn('No menu items found after maximum attempts');
            return;
          }

          menuItems.forEach((menuItem) => {
            const menuText = menuItem.textContent?.trim() || '';

            // Check if this menu item should trigger our megamenu
            const shouldTrigger = this.shouldTriggerForMenuItem(menuText);

            if (shouldTrigger) {
              // Hover events
              const mouseEnterHandler = () => {
                this.activeMenuElement = menuItem;
                this.showMegamenu();
                this.clearHideTimeout();
                this.isMouseInSafeZone = true;
              };

              const mouseLeaveHandler = () => {
                // Immediately set safe zone to false when leaving menu item
                this.isMouseInSafeZone = false;
                // Use intelligent hide logic that considers safe zones
                this.handleIntelligentHide();
              };

              // Click events (for mobile)
              const clickHandler = (e) => {
                if (window.innerWidth < 768) {
                  // Mobile breakpoint
                  e.preventDefault();
                  this.toggleMegamenu();
                }
              };

              menuItem.addEventListener('mouseenter', mouseEnterHandler);
              menuItem.addEventListener('mouseleave', mouseLeaveHandler);
              menuItem.addEventListener('click', clickHandler);

              // Store for cleanup
              this.eventListeners.push({
                element: menuItem,
                events: [
                  { type: 'mouseenter', handler: mouseEnterHandler },
                  { type: 'mouseleave', handler: mouseLeaveHandler },
                  { type: 'click', handler: clickHandler }
                ]
              });
            }
          });

          // Also set up hover listeners for ALL menu items to close other megamenus
          // Exclude links that are inside megamenu containers
          // Prioritize menu items inside reparent_to_selector if available
          let allMenuItems;
          if (settings.reparent_to_selector) {
            const reparentContainer = document.querySelector(settings.reparent_to_selector);
            if (reparentContainer) {
              allMenuItems = reparentContainer.querySelectorAll('nav a, .menu a, [data-menu-item]');
            }
          }

          // Fallback to global search if no reparent container or no items found
          if (!allMenuItems || allMenuItems.length === 0) {
            allMenuItems = document.querySelectorAll('nav a, .menu a, [data-menu-item]');
          }
          allMenuItems.forEach((menuItem) => {
            const menuText = menuItem.textContent?.trim() || '';
            const shouldTrigger = this.shouldTriggerForMenuItem(menuText);

            // Check if this menu item is inside a megamenu - if so, skip it
            const isInsideMegamenu = menuItem.closest('[x-data*="megamenu"]') !== null;

            // If this menu item doesn't trigger our megamenu AND is not inside a megamenu, it should close it
            if (!shouldTrigger && !isInsideMegamenu) {
              const mouseEnterHandler = () => {
                this.hideMegamenu();
                this.activeMenuElement = null;
              };

              menuItem.addEventListener('mouseenter', mouseEnterHandler);

              this.eventListeners.push({
                element: menuItem,
                events: [{ type: 'mouseenter', handler: mouseEnterHandler }]
              });
            }
          });

          // Set up megamenu hover listeners to keep it open
          this.setupMegamenuHoverListeners();
        };

        // Start the setup process with initial delay to allow React to render
        setTimeout(() => attemptSetup(), 100);
      },

      // Set up megamenu's own hover listeners
      setupMegamenuHoverListeners() {
        const mouseEnterHandler = () => {
          this.clearHideTimeout();
          this.isMouseInSafeZone = true;
        };

        const mouseLeaveHandler = () => {
          this.isMouseInSafeZone = false;
          this.handleIntelligentHide();
        };

        // Attach to the content area (.megamenu-safe-zone) instead of the full-width container
        const safeZoneElement = this.$el.querySelector('.megamenu-safe-zone');
        if (safeZoneElement) {
          safeZoneElement.addEventListener('mouseenter', mouseEnterHandler);
          safeZoneElement.addEventListener('mouseleave', mouseLeaveHandler);

          this.eventListeners.push({
            element: safeZoneElement,
            events: [
              { type: 'mouseenter', handler: mouseEnterHandler },
              { type: 'mouseleave', handler: mouseLeaveHandler }
            ]
          });
        } else {
          // Fallback to the main element if safe zone not found
          this.$el.addEventListener('mouseenter', mouseEnterHandler);
          this.$el.addEventListener('mouseleave', mouseLeaveHandler);

          this.eventListeners.push({
            element: this.$el,
            events: [
              { type: 'mouseenter', handler: mouseEnterHandler },
              { type: 'mouseleave', handler: mouseLeaveHandler }
            ]
          });
        }
      },

      // Intelligent hide logic that considers safe zones and gaps
      handleIntelligentHide() {
        this.clearHideTimeout();

        // Use a slightly longer delay to prevent flickering from rapid mouse movements
        this.hoverLeaveTimeout = setTimeout(() => {
          // If mouse is not in safe zone, hide the megamenu
          if (!this.isMouseInSafeZone && this.isVisible) {
            this.hideMegamenu();
            this.activeMenuElement = null;
          }
        }, 100); // Shorter delay for better responsiveness

        // Also set up a longer timeout as fallback to ensure hiding
        setTimeout(() => {
          if (!this.isMouseInSafeZone && this.isVisible) {
            this.hideMegamenu();
            this.activeMenuElement = null;
          }
        }, 300); // Shorter fallback delay
      },

      // Check if menu item should trigger this megamenu
      shouldTriggerForMenuItem(menuText) {
        // Check by title
        if (this.targetMenuTitle && menuText.toLowerCase() === this.targetMenuTitle.toLowerCase()) {
          return true;
        }

        return false;
      },

      // Show megamenu and hide others
      showMegamenu() {
        // Don't show megamenu if block builder is active or already transitioning
        if (this.isBlockBuilderActive || this.isTransitioning) {
          return;
        }

        this.isTransitioning = true;

        // Hide all other megamenus first
        this.hideOtherMegamenus();

        // Clear all existing grey styling before applying new styling
        this.removeGreyingFromAllMenuItems();

        // Apply grey styling to other target menu items
        this.applyGreyingToOtherMenuItems();

        // Show this megamenu
        this.isVisible = true;
        this.clearHideTimeout();

        // Reset transitioning state after animation duration
        setTimeout(() => {
          this.isTransitioning = false;
        }, parseInt(settings.animation_duration || 300) + 50);
      },

      // Hide all other megamenu instances
      hideOtherMegamenus() {
        this.static.instances.forEach((instance) => {
          if (instance !== this && instance.isVisible) {
            instance.hideMegamenu();
          }
        });
      },

      // Apply grey styling to other target menu items (not the current one)
      applyGreyingToOtherMenuItems() {
        // Get all megamenu elements and their target titles
        const allMegamenus = document.querySelectorAll('[x-data*="megamenu"]');
        const otherTargetTitles = [];

        allMegamenus.forEach((megamenuEl) => {
          try {
            // Get the Alpine component data
            const component = megamenuEl._x_dataStack?.[0];
            if (component && component.targetMenuTitle && component !== this) {
              otherTargetTitles.push(component.targetMenuTitle);
            }
          } catch (e) {
            // Skip if we can't access the component data
          }
        });

        // Apply grey styling to menu items that match other megamenu targets
        const menuItems = document.querySelectorAll('nav a, .menu a, [data-menu-item]');
        menuItems.forEach((menuItem) => {
          const menuText = menuItem.textContent?.trim() || '';

          // Skip links that are inside megamenu containers or outside header
          if (menuItem.closest('[x-data*="megamenu"]') || !menuItem.closest('[x-data*="header"]')) {
            return;
          }

          // Don't grey out the current active megamenu's target menu item
          if (menuText.toLowerCase() === this.targetMenuTitle.toLowerCase()) {
            return;
          }

          // Check if this menu item matches any other megamenu's target
          if (otherTargetTitles.some((targetTitle) => menuText.toLowerCase() === targetTitle.toLowerCase())) {
            menuItem.classList.add('megamenu-greyed');
          }
        });
      },

      // Remove grey styling from all menu items
      removeGreyingFromAllMenuItems() {
        const menuItems = document.querySelectorAll('nav a, .menu a, [data-menu-item]');
        menuItems.forEach((menuItem) => {
          // Skip footer links for consistency
          if (menuItem.closest('footer') || menuItem.closest('[class*="footer"]')) {
            return;
          }
          menuItem.classList.remove('megamenu-greyed');
        });
      },

      // Check if any megamenu instance is currently visible
      anyMegamenuVisible() {
        // Check all megamenu elements in the DOM for visibility
        const allMegamenus = document.querySelectorAll('[x-data*="megamenu"]');

        for (const megamenuEl of allMegamenus) {
          try {
            const component = megamenuEl._x_dataStack?.[0];
            if (component && component.isVisible) {
              return true;
            }
          } catch (e) {
            // Skip if we can't access the component data
          }
        }

        return false;
      },

      // Hide megamenu
      hideMegamenu() {
        // Prevent rapid hide calls during transition
        if (this.isTransitioning) {
          return;
        }

        this.isTransitioning = true;
        this.isVisible = false;
        this.isMouseInSafeZone = false;

        // Remove grey styling from all menu items when no megamenu is visible
        if (!this.anyMegamenuVisible()) {
          this.removeGreyingFromAllMenuItems();
        }

        // Reset transitioning state after animation duration
        setTimeout(() => {
          this.isTransitioning = false;
        }, parseInt(settings.animation_duration || 300) + 50);
      },

      // Toggle megamenu (for mobile clicks)
      toggleMegamenu() {
        this.isVisible = !this.isVisible;
        if (!this.isVisible) {
          this.isMouseInSafeZone = false;
        }
      },

      // Schedule megamenu hide with delay (legacy method, kept for compatibility)
      scheduleMegamenuHide() {
        this.handleIntelligentHide();
      },

      // Clear hide timeout
      clearHideTimeout() {
        if (this.hoverLeaveTimeout) {
          clearTimeout(this.hoverLeaveTimeout);
          this.hoverLeaveTimeout = null;
        }
      },

      // Handle resize events
      setupResizeListener() {
        let resizeTimeout;
        const resizeHandler = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.renderTypeKey++; // Force reactivity update
          }, 100);
        };

        window.addEventListener('resize', resizeHandler);

        this.eventListeners.push({
          element: window,
          events: [{ type: 'resize', handler: resizeHandler }]
        });
      },

      // Handle max height calculation for screen_minus_header type
      setupReparentHeightCalculation() {
        if (settings.max_height_type !== 'screen_minus_header' || !settings.reparent_to_selector) {
          return;
        }

        const reparentElement = document.querySelector(settings.reparent_to_selector);

        if (reparentElement) {
          const updateHeight = () => {
            const height = reparentElement.offsetHeight;
            this.$el.style.maxHeight = `calc(100dvh - ${height}px)`;
          };

          updateHeight();

          const resizeObserver = new ResizeObserver(updateHeight);
          resizeObserver.observe(reparentElement);

          this.reparentResizeObserver = resizeObserver;
        }
      }
    };
  }
};
